import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useApp } from '../context/AppContext';
import { normalizePhone, validatePassword } from '../utils/inputValidation';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { resetPhone, setResetPhone } = useApp();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [verifiedMsg, setVerifiedMsg] = useState(true);

  const [showPass1, setShowPass1] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  const passwordsMatch = password.length > 0 && confirm.length > 0 && password === confirm;

  const vibrate = (ms = 100) => {
    if (navigator?.vibrate) navigator.vibrate(ms);
  };

  const getPasswordStrength = () => {
    let score = 0;
    if (password.length >= 6) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };
  const strength = getPasswordStrength();

  useEffect(() => {
    const t = setTimeout(() => setVerifiedMsg(false), 2000);
    return () => clearTimeout(t);
  }, []);

  const handleReset = async () => {
    if (!passwordsMatch) {
      vibrate();
      setMessage("Las contraseñas no coinciden.");
      return;
    }

    if (password.length < 6) {
      vibrate();
      setMessage("La contraseña debe tener mínimo 6 caracteres.");
      return;
    }

    // 🔥 Mandamos la clave CRUDA (Postgres la encripta con su trigger)
    const { error } = await supabase.rpc('reset_user_password', {
      p_phone: normalizePhone(resetPhone),
      p_new_password: validatePassword(password)
    });

    if (error) {
      console.error(error);
      vibrate();
      setMessage(error.message?.includes('RESET_NOT_VERIFIED')
        ? 'La sesión de recuperación expiró. Solicita un nuevo código.'
        : 'Error al actualizar la contraseña.');
      return;
    }

    setResetPhone(null);

    setMessage("¡Contraseña actualizada con éxito!");
    setTimeout(() => navigate("/login"), 1200);
  };

  return (
    <div className="min-h-screen px-6 py-12 bg-gray-50">
      <div className="max-w-md mx-auto">
        {/* Cabecera */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center mb-10"
        >
          <button
            onClick={() => navigate('/verify-reset-code')}
            className="p-3 rounded-full hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold ml-4 text-gray-800">Nueva Contraseña</h1>
        </motion.div>

        {/* Tarjeta principal */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-3xl shadow-lg p-8"
        >
          {/* Mensaje temporal de verificación */}
          {verifiedMsg && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-6 p-4 bg-green-100 text-green-700 rounded-2xl font-medium"
            >
              Código verificado correctamente ✔️
            </motion.div>
          )}

          <h2 className="text-xl font-semibold text-center text-gray-800 mb-3">
            Crea una contraseña segura
          </h2>
          <p className="text-center text-gray-600 mb-8 leading-relaxed">
            Usa al menos 6 caracteres, combina mayúsculas, números y símbolos para mayor seguridad.
          </p>

          {/* Mensaje de error */}
          {message && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-red-600 font-medium mb-6"
            >
              {message}
            </motion.p>
          )}

          {/* Campo nueva contraseña */}
          <div className="relative mb-4">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type={showPass1 ? "text" : "password"}
              placeholder="Nueva contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-14 pr-14 py-5 bg-gray-50 border-2 border-gray-300 rounded-2xl focus:border-blue-900 focus:outline-none transition-all text-lg"
            />
            <button
              type="button"
              onClick={() => setShowPass1(!showPass1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPass1 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Barra de fuerza de contraseña */}
          <div className="h-3 w-full bg-gray-200 rounded-full mb-6 overflow-hidden">
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${(strength / 4) * 100}%`,
                backgroundColor:
                  strength <= 1 ? "#ef4444" : // red-500
                  strength === 2 ? "#f97316" : // orange-500
                  strength === 3 ? "#eab308" : // yellow-500
                  "#22c55e" // green-500
              }}
            />
          </div>
          <p className="text-sm text-center text-gray-600 mb-6">
            Fuerza de la contraseña:{' '}
            <span className="font-medium">
              {strength <= 1 ? "Débil" :
               strength === 2 ? "Media" :
               strength === 3 ? "Buena" :
               "Fuerte"}
            </span>
          </p>

          {/* Campo confirmar contraseña */}
          <div className="relative mb-4">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type={showPass2 ? "text" : "password"}
              placeholder="Confirmar contraseña"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full pl-14 pr-14 py-5 bg-gray-50 border-2 border-gray-300 rounded-2xl focus:border-blue-900 focus:outline-none transition-all text-lg"
            />
            <button
              type="button"
              onClick={() => setShowPass2(!showPass2)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPass2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Indicador de coincidencia */}
          {confirm.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center mb-8"
            >
              {passwordsMatch ? (
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600 mr-3" />
              )}
              <span className={`font-medium ${passwordsMatch ? "text-green-600" : "text-red-600"}`}>
                {passwordsMatch ? "Las contraseñas coinciden" : "Las contraseñas no coinciden"}
              </span>
            </motion.div>
          )}

          {/* Botón guardar */}
          <button
            onClick={handleReset}
            disabled={!passwordsMatch}
            className={`w-full py-5 rounded-2xl text-white font-bold text-lg transition-all duration-200 shadow-md ${
              passwordsMatch
                ? "bg-blue-900 hover:bg-blue-800"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            Guardar contraseña
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;

